/**
 * @fileoverview Canonical inspection template types and module gating taxonomy.
 * @module core-domain/inspection/templateTypes
 *
 * Responsibilities:
 * - Define canonical TemplateType union and display labels.
 * - Map feature modules to supported template types.
 * - Provide unified case-type / template-type options for dropdowns across
 *   mobile-pwa and tenant-web.
 *
 * Dependencies: ../forms/lookupFiltering.
 *
 * Architecture:
 * - Shared package layer: canonical source of truth for template types across hosts.
 * - Synchronizes template filtering and case-type selection in mobile-pwa and tenant-web.
 *
 * Notes:
 * - 'custom' is a core type that is always enabled regardless of module tiers.
 * - Module-code keys are canonical registry codes (MODULE); historical
 *   spellings are accepted as input aliases but never emitted.
 */
import {
  normalizeLookupInspectionModules,
  type LookupInspectionModuleKey,
} from '../forms/lookupFiltering';
import { MODULE } from '../modules/moduleRegistry';

/**
 * @contract MODULE_FAMILY_BY_SPELLING
 * Purpose: Collapses every spelling of an inspection module onto one family so
 *   template-to-case-type matching works whichever vocabulary the data uses.
 * Fields:  Lower-cased spelling -> canonical module code (MODULE registry).
 * Rules:   The legacy template taxonomy (`fireCode`, `communityRisk`,
 *          `construction`) and historical module codes (`fire-code`,
 *          `community-risk-reduction`) all collapse onto the canonical
 *          registry codes. Lookup normalization may emit either vocabulary,
 *          so matching compares families, never raw keys.
 * Sync:    Pure lookup table; no persistence.
 */
const MODULE_FAMILY_BY_SPELLING: Record<string, string> = {
  wildland: MODULE.WILDLAND_FIRE,
  wildlandfire: MODULE.WILDLAND_FIRE,
  'wildland-fire': MODULE.WILDLAND_FIRE,
  firecode: MODULE.FIRE_CODE_COMPLIANCE,
  'fire-code': MODULE.FIRE_CODE_COMPLIANCE,
  'fire-code-compliance': MODULE.FIRE_CODE_COMPLIANCE,
  firecodecompliance: MODULE.FIRE_CODE_COMPLIANCE,
  'fire-codecompliance': MODULE.FIRE_CODE_COMPLIANCE,
  building: MODULE.FIRE_CODE_COMPLIANCE,
  construction: MODULE.BUILDING_CODE_COMPLIANCE,
  'building-code-compliance': MODULE.BUILDING_CODE_COMPLIANCE,
  buildingcodecompliance: MODULE.BUILDING_CODE_COMPLIANCE,
  hydrant: MODULE.HYDRANT_MANAGEMENT,
  'hydrant-management': MODULE.HYDRANT_MANAGEMENT,
  hydrantmanagement: MODULE.HYDRANT_MANAGEMENT,
  communityrisk: MODULE.COMMUNITY_RISK_REDUCTION,
  'community-risk': MODULE.COMMUNITY_RISK_REDUCTION,
  'community-risk-reduction': MODULE.COMMUNITY_RISK_REDUCTION,
  communityriskreduction: MODULE.COMMUNITY_RISK_REDUCTION,
  publiceducation: MODULE.COMMUNITY_RISK_REDUCTION,
  'public-education': MODULE.COMMUNITY_RISK_REDUCTION,
};

/** Family code for a module key, or undefined when the key is not a known template module. */
function knownModuleFamily(key: string | null | undefined): string | undefined {
  const spelling = String(key ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  return spelling ? MODULE_FAMILY_BY_SPELLING[spelling] : undefined;
}

/** Family for matching: known modules collapse to their family; other real catalog codes stand for themselves. */
function moduleFamilyForMatching(key: string): string {
  return knownModuleFamily(key) ?? String(key).trim().toLowerCase();
}

/**
 * @contract TemplateType
 * Purpose: Classifies inspection templates for module-aware filtering.
 * Fields:  custom, wildland, fireCode, hydrant, complex, communityRisk.
 * Rules:   Values are template taxonomy keys, not case-type lookup codes.
 * Sync:    Shared domain contract used by tenant-web and mobile-pwa.
 */
export type TemplateType = 'custom' | 'wildland' | 'fireCode' | 'hydrant' | 'complex' | 'communityRisk' | 'homeSafetySurvey';

/**
 * @contract TEMPLATE_TYPE_LOOKUP_MODULE_KEYS
 * Purpose: Maps template types to persisted case-type lookup module keys.
 * Fields:  One module-key list for each canonical TemplateType.
 * Rules:   Custom templates have no module restriction; complex templates span
 *          fire-code and construction lookup tags.
 * Sync:    Used by shared case-type/template-type filtering.
 */
export const TEMPLATE_TYPE_LOOKUP_MODULE_KEYS: Record<TemplateType, LookupInspectionModuleKey[]> = {
  custom: [],
  wildland: ['wildland'],
  fireCode: ['fireCode'],
  complex: ['fireCode', 'construction'],
  hydrant: ['hydrant'],
  communityRisk: ['communityRisk'],
  // Home safety surveys match the same community-risk-reduction case-type
  // lookup family — the survey is a public-education program activity.
  homeSafetySurvey: ['communityRisk'],
};

/**
 * @process getTemplateLookupModuleKeys
 *
 * Steps:
 * 1. Resolve canonical template types case-insensitively.
 * 2. Normalize non-canonical values as lookup inspection-module aliases.
 * Inputs: Template type, category, or module key.
 * Outputs: Normalized lookup inspection-module keys.
 * Failure Handling: Empty and unknown values return an empty list.
 * Idempotency: REQUIRED — same input always returns the same keys.
 */
export function getTemplateLookupModuleKeys(
  templateType: string | null | undefined,
): LookupInspectionModuleKey[] {
  const normalized = String(templateType ?? '').trim();
  if (!normalized) return [];

  const canonicalType = ALL_TEMPLATE_TYPES.find(
    (type) => type.toLowerCase() === normalized.toLowerCase(),
  );
  if (canonicalType) return [...TEMPLATE_TYPE_LOOKUP_MODULE_KEYS[canonicalType]];

  // Non-canonical values count only when they are a KNOWN module spelling.
  // Lookup normalization passes unknown codes through verbatim, so without this
  // filter an unclassified template type would demand a module nobody has and
  // vanish for every case type instead of staying globally applicable.
  return normalizeLookupInspectionModules([normalized]).filter((key) => knownModuleFamily(key) !== undefined);
}

/**
 * @process doesTemplateMatchCaseType
 *
 * Steps:
 * 1. Treat an unselected case type as unrestricted.
 * 2. Normalize case-type and template-type lookup module keys.
 * 3. Keep untagged case types and templates globally applicable.
 * 4. Otherwise require at least one shared module key.
 * Inputs: Template type and optional selected case-type module keys.
 * Outputs: Whether the template is applicable to the selected case type.
 * Failure Handling: Unknown/empty template types remain globally applicable.
 * Idempotency: REQUIRED — same inputs always return the same result.
 */
export function doesTemplateMatchCaseType(
  templateType: string | null | undefined,
  caseTypeModuleKeys: ReadonlyArray<string | null | undefined> | null | undefined,
): boolean {
  if (caseTypeModuleKeys == null) return true;

  const normalizedCaseTypeKeys = normalizeLookupInspectionModules([...caseTypeModuleKeys]);
  if (normalizedCaseTypeKeys.length === 0) return true;

  const templateModuleKeys = getTemplateLookupModuleKeys(templateType);
  if (templateModuleKeys.length === 0) return true;

  const caseTypeFamilies = new Set(normalizedCaseTypeKeys.map(moduleFamilyForMatching));
  return templateModuleKeys.some((key) => caseTypeFamilies.has(moduleFamilyForMatching(key)));
}

/**
 * Display labels for each template type.
 */
export const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  custom: 'Custom',
  wildland: 'Wildland',
  fireCode: 'Fire Code',
  hydrant: 'Hydrant',
  complex: 'Complex',
  communityRisk: 'Community Risk',
  homeSafetySurvey: 'Home Safety Survey',
};

/** Template types that are always available regardless of modules */
export const CORE_TEMPLATE_TYPES: TemplateType[] = ['custom'];

/** Standard default template types when no specific module set is configured */
export const DEFAULT_TEMPLATE_TYPES: TemplateType[] = [
  'custom',
  'wildland',
  'fireCode',
  'complex',
];

/** All known template types in canonical display order */
export const ALL_TEMPLATE_TYPES: TemplateType[] = [
  'custom',
  'wildland',
  'fireCode',
  'complex',
  'hydrant',
  'communityRisk',
  'homeSafetySurvey',
];

/** Template types gated by specific modules */
export const MODULE_TEMPLATE_TYPES: Record<string, TemplateType[]> = {
  [MODULE.WILDLAND_FIRE]: ['wildland'],
  [MODULE.FIRE_CODE_COMPLIANCE]: ['fireCode'],
  [MODULE.HYDRANT_MANAGEMENT]: ['hydrant'],
  [MODULE.BUILDING_CODE_COMPLIANCE]: ['fireCode', 'complex'],
  [MODULE.COMMUNITY_RISK_REDUCTION]: ['communityRisk', 'homeSafetySurvey'],
  [MODULE.PERMITS_CERTIFICATES]: [],
};

export const ALL_MODULE_TEMPLATE_ENTRIES: Array<{ module: string; type: TemplateType }> = [
  { module: MODULE.WILDLAND_FIRE, type: 'wildland' },
  { module: MODULE.FIRE_CODE_COMPLIANCE, type: 'fireCode' },
  { module: MODULE.HYDRANT_MANAGEMENT, type: 'hydrant' },
  { module: MODULE.BUILDING_CODE_COMPLIANCE, type: 'fireCode' },
  { module: MODULE.BUILDING_CODE_COMPLIANCE, type: 'complex' },
  { module: MODULE.COMMUNITY_RISK_REDUCTION, type: 'communityRisk' },
  { module: MODULE.COMMUNITY_RISK_REDUCTION, type: 'homeSafetySurvey' },
];

/**
 * Returns template/form types that are enabled for the given modules.
 * If enabledModules is not provided (undefined), returns ALL_TEMPLATE_TYPES.
 * If enabledModules is an empty array ([]), returns CORE_TEMPLATE_TYPES (['custom']).
 */
export function getEnabledTemplateTypes(enabledModules?: string[]): TemplateType[] {
  if (enabledModules === undefined) {
    return ALL_TEMPLATE_TYPES;
  }
  const types: TemplateType[] = [...CORE_TEMPLATE_TYPES];
  for (const mod of enabledModules) {
    const modTypes = MODULE_TEMPLATE_TYPES[mod];
    if (modTypes) {
      for (const t of modTypes) {
        if (!types.includes(t)) types.push(t);
      }
    }
  }
  return types;
}

/**
 * Checks if a given template/form type is enabled.
 */
export function isTemplateTypeEnabled(type: TemplateType, enabledModules?: string[]): boolean {
  if (enabledModules === undefined) return true;
  if ((CORE_TEMPLATE_TYPES as string[]).includes(type)) return true;
  return ALL_MODULE_TEMPLATE_ENTRIES
    .filter((e) => e.type === type)
    .some((e) => enabledModules.includes(e.module));
}

/**
 * Returns the module key that gates a given template type, or null if it's core.
 */
export function getModuleForTemplateType(type: TemplateType): string | null {
  const entry = ALL_MODULE_TEMPLATE_ENTRIES.find((e) => e.type === type);
  return entry ? entry.module : null;
}

/**
 * Returns unified case-type / template-type dropdown options matching Image 3 (Custom, Wildland, Fire Code, Complex, etc.)
 */
export function getCaseTypeOptions(enabledModules?: string[]): Array<{ code: TemplateType; name: string }> {
  const types = enabledModules !== undefined
    ? getEnabledTemplateTypes(enabledModules)
    : DEFAULT_TEMPLATE_TYPES;
  return types.map((type) => ({
    code: type,
    name: TEMPLATE_TYPE_LABELS[type] ?? type,
  }));
}
